import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { displayDialog, closeDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { DialogProps } from "models/propTypes/dialogPropTypes";
import { DialogEnums, StyleEnums, CurrencyEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import cssExports from "theme/styles/main.module.scss";
//#endregion
//#region components
import { useCurrencyService } from "serviceHooks";
import { LocalizationHelperInstance } from "utils/helpers";
//#endregion

const USER_CANCELLED_ERROR = "User cancelled";

/**
 * Custom hook for handling high invoice amount warnings
 * This hook provides functionality to check invoice amounts and display warning dialogs
 */
function useHighInvoiceAmountWarning() {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const currencyService = useCurrencyService();

  const getHighInvoiceAmountDialog = (
    amount: number,
    currency: string,
    onConfirm: () => void | Promise<void>,
    amountInSEK?: number,
    onCancel?: () => void
  ): DialogProps => {
    const currencySymbol = currency?.toUpperCase() === CurrencyEnums.CurrencyCode.SEK ? "kr" : currency?.toUpperCase();

    const dialog: DialogProps = {
      isOpen: false,
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HighAmountWarningTitle),
      titleIcon: IconDefinitionConstants.WExclamationTriangle,
      dialogType: DialogEnums.DialogType.Content,
      maxWidth: "sm",
      hideCancelButton: false,
      hideCloseButton: false,
      children: (
        <>
          <p>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HighAmountWarningMessage)}</p>
          <p>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HighAmountWarningTotalLabel)}{" "}
            <span className={cssExports.textBlack}>
              {LocalizationHelperInstance.getFormattedNumber(amount, 2, 2)} {currencySymbol}
            </span>
          </p>
        </>
      ),
      actions: [
        {
          text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.HighAmountWarningConfirm),
          type: StyleEnums.DisplayType.Info,
          onClick: () => Promise.resolve(onConfirm()),
        },
      ],
    };

    if (onCancel) {
      dialog.onClose = onCancel;
    }

    return dialog;
  };

  /**
   * Checks if the invoice amount exceeds the threshold (converts to SEK if needed)
   * @returns Object with amountInSEK if threshold exceeded, null otherwise
   */
  const checkHighInvoiceAmount = async (amount: number, currency: string, invoiceDate?: Date) => {
    if (!currency || amount <= 0) return null;

    const currencyUpper = currency.toUpperCase();
    let amountInSEK = amount;

    if (currencyUpper !== CurrencyEnums.CurrencyCode.SEK) {
      try {
        const exchangeRate = await currencyService.getExchangeRate(currency, invoiceDate || new Date());
        amountInSEK = amount * exchangeRate;
      } catch (error) {
        console.error("Failed to get exchange rate:", error);
        return null;
      }
    }

    if (amountInSEK > InvoiceConstants.HighInvoiceAmountThresholdSEK) {
      return { amountInSEK: currencyUpper !== CurrencyEnums.CurrencyCode.SEK ? amountInSEK : undefined };
    }

    return null;
  };

  /**
   * Shows a warning dialog if the amount is high, otherwise executes the callback directly
   * @param amount - The invoice amount
   * @param currency - The currency code
   * @param onConfirm - The callback to execute when user confirms or if no warning needed
   * @param invoiceDate - Optional invoice date for currency conversion
   * @returns Promise that resolves with the callback result or rejects if cancelled
   */
  const showWarningDialogIfNeeded = async <T,>(
    amount: number,
    currency: string,
    onConfirm: () => T | Promise<T>,
    invoiceDate?: Date
  ): Promise<T | undefined> => {
    const highAmountCheck = await checkHighInvoiceAmount(amount, currency, invoiceDate);

    if (highAmountCheck) {
      return new Promise<T | undefined>((resolve, reject) => {
        dispatch(
          displayDialog(
            getHighInvoiceAmountDialog(
              amount,
              currency,
              async () => {
                try {
                  const result = await onConfirm();
                  resolve(result);
                } catch (error) {
                  console.error("Error in dialog confirm callback:", error);
                  dispatch(closeDialog());
                  reject(error);
                }
              },
              highAmountCheck.amountInSEK,
              () => {
                reject(new Error(USER_CANCELLED_ERROR));
              }
            )
          )
        );
      });
    }

    // Amount is not high - execute callback directly
    return await onConfirm();
  };

  return {
    checkHighInvoiceAmount,
    showWarningDialogIfNeeded,
    USER_CANCELLED_ERROR,
  };
}

export default useHighInvoiceAmountWarning;
